import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { Embajador } from '@/types';

export function useAmbassador(slug?: string) {
  const [ambassador, setAmbassador] = useState<Embajador | null>(null);
  const [leader, setLeader] = useState<Embajador | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    async function fetchAmbassadorData() {
      setLoading(true);
      try {
        // 1. Fetch the leader (first registered)
        const { data: leaderData, error: leaderError } = await supabase
          .from('embajadores')
          .select('*, puntos_venta(*)')
          .order('id', { ascending: true })
          .limit(1)
          .single();

        if (leaderError) throw leaderError;
        
        // El objeto leader ya debe tener todas las propiedades correctas según db.sql
        const processedLeader = leaderData as Embajador;
        setLeader(processedLeader);

        // 2. If slug is provided, fetch the specific ambassador
        let currentAmbassador = processedLeader;
        if (slug && slug !== 'undefined' && slug !== processedLeader.slug) {
          const { data: ambData, error: ambError } = await supabase
            .from('embajadores')
            .select('*, puntos_venta(*)')
            .eq('slug', slug)
            .single();

          if (!ambError && ambData) {
            currentAmbassador = ambData as Embajador;
          } else {
            console.warn(`Ambassador with slug ${slug} not found, falling back to leader.`);
          }
        }

        setAmbassador(currentAmbassador);
      } catch (err) {
        console.error('Error fetching ambassador data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }

    fetchAmbassadorData();
  }, [slug]);

  // Derived data based on fallback rules
  const displayData = ambassador && leader ? {
    ...ambassador,
    // Garantizar campos para la UI usando leader como respaldo si faltan
    nombre_completo: ambassador.nombre_completo || leader.nombre_completo,
    rango: ambassador.rango || leader.rango,
    whatsapp_number: ambassador.whatsapp_number || leader.whatsapp_number,
    
    // Si no hay inicio_video_url, usar el video, nombre y rango del líder
    inicio_video_url: ambassador.inicio_video_url || leader.inicio_video_url,
    embajador_video_url: ambassador.embajador_video_url || leader.embajador_video_url,
    
    // Si el video falta, mostramos nombre y rango del líder para ESE video
    showLeaderVideoInfoInicio: !ambassador.inicio_video_url,
    showLeaderVideoInfoEmbajador: !ambassador.embajador_video_url,
    leaderName: leader.nombre_completo,
    leaderRank: leader.rango,
  } : null;

  return { ambassador: displayData, loading, error, leader };
}
